import DOMPurify from 'dompurify';

import { escapeRegex } from './escapeRegex';

/**
 * Highlights substring matches in text by wrapping them in <mark> tags.
 * Supports both exact substring matching (default) and fuzzy word matching.
 * Input text is always sanitized with DOMPurify to prevent XSS attacks.
 *
 * @param text - The text to highlight matches in (will be sanitized)
 * @param searchTerm - The search term to match (case-insensitive)
 * @param options - Optional configuration
 * @param options.fuzzy - If true, splits search term by spaces and highlights each word independently
 * @returns HTML string with <mark> tags around matches, fully sanitized
 *
 * @example
 * // Exact substring matching (default)
 * highlightText('Time and Material', 'and')
 * // 'Time <mark>and</mark> Material'
 *
 * highlightText('In Progress', 'prog')
 * // 'In <mark>Prog</mark>ress'
 *
 * // Fuzzy word matching
 * highlightText('Time and Material', 'time mat', { fuzzy: true })
 * // '<mark>Time</mark> and <mark>Mat</mark>erial'
 *
 * highlightText('In Progress', 'in pro', { fuzzy: true })
 * // '<mark>In</mark> <mark>Pro</mark>gress'
 */
export const highlightText = (
  text: string,
  searchTerm: string,
  options?: { fuzzy?: boolean },
): string => {
  // Sanitize input text first to prevent XSS
  const sanitizedText = DOMPurify.sanitize(text, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
  });

  // Early return for empty inputs (sanitized)
  if (!searchTerm || !sanitizedText) {
    return sanitizedText;
  }

  let pattern: RegExp;

  if (options?.fuzzy) {
    // Fuzzy mode: split search term into words and match each independently
    const words = searchTerm
      .split(' ')
      .filter((word) => word.trim())
      .map((word) => escapeRegex(word));

    // If no valid words after filtering, return sanitized text
    if (words.length === 0) {
      return sanitizedText;
    }

    // Create pattern: (word1|word2|word3) with case-insensitive, global flags
    pattern = new RegExp(`(${words.join('|')})`, 'gi');
  } else {
    // Exact mode: match the entire search term as a substring
    const escapedTerm = escapeRegex(searchTerm);
    pattern = new RegExp(`(${escapedTerm})`, 'gi');
  }

  // Replace matches with <mark> tags
  const highlighted = sanitizedText.replace(pattern, '<mark>$1</mark>');

  // Sanitize again to ensure only <mark> tags remain
  return DOMPurify.sanitize(highlighted, {
    ALLOWED_TAGS: ['mark'],
    ALLOWED_ATTR: [],
  });
};
